import React, { useState, useEffect } from "react";
import { Box, Typography, IconButton, Tooltip } from "@mui/material";
import { AccessTime, EventNote, Edit } from "@mui/icons-material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import dayjs from "dayjs";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestDetailHeader } from "components/DesignSystem/RequestDetailHeader";
import { RequestEditHeader } from "components/DesignSystem/RequestEditHeader";
import { RequestStatusTimeline } from "components/DesignSystem/RequestStatusTimeline";
import { RequestCancelButton } from "components/DesignSystem/RequestCancelButton";
import RequestDetailsSection from "components/DesignSystem/RequestDetailsSection";
import { detailAccentPalette } from "components/DesignSystem/RequestDetailField";
import { GlobalConfirmDialog } from "components/DesignSystem/sharedUiPatterns";
import { CustomTextBox, CustomAutoComplete } from "components/CustomComponents";
import { CustomizedDatePicker } from "components/CustomComponents/CustomizedDatePicker";
import {
  PrimaryButton,
  SecondaryButton,
} from "components/CustomComponents/DesignSystemButtons";
import { infoStyle, childBoxStyle } from "services/constants";
import { tooltipStyleProps } from "services/constants/Styles";
import { atleastOneCharacterWithSpace } from "services/constants/patterns";
import { minimum3Characters } from "services/constants/ErrorMessages";
import { customAPI } from "services/utils/CustomAPI";
import { showToast } from "services/utils/Status";
import { normalizeApprovalPersonFields } from "pages/TeamZone/Approve/displayNameUtils";
import moment from "moment";

const dayoffType = [{ label: "Week Off" }, { label: "Holiday" }];

const formatDayOffDateTime = (...values) => {
  const value = values.find(Boolean);
  if (!value) return undefined;
  const parsed = moment(
    value,
    ["DD-MM-YYYY, HH:mm", "DD-MM-YYYY, hh:mm A", "YYYY-MM-DD HH:mm:ss", moment.ISO_8601],
    true,
  );
  return parsed.isValid() ? parsed.format("DD MMM YYYY, hh:mm A") : value;
};

export const ViewDayOffPostApproval = ({
  open = true,
  selectedData,
  onClose,
  onUpdated,
  checkAccess,
}) => {
  const requestCompanyId = useSelector((state) => state.tokenReducer.topbarCompanyId);
  const { domain, userDetails } = useSelector((state) => state.tokenReducer);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showEditOption, setShowEditOption] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelReasonError, setCancelReasonError] = useState("");

  const {
    setValue,
    handleSubmit,
    register,
    watch,
    control,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      dayoff_type: "",
      date: null,
      reason_for_dayoff: "",
    },
  });

  useEffect(() => {
    if (showEditOption) {
      setValue("dayoff_type", selectedData?.day_type);
      setValue(
        "date",
        selectedData?.date
          ? dayjs(selectedData.date.split("-").reverse().join("-"))
          : null,
      );
      setValue("reason_for_dayoff", selectedData?.reason);
    }
  }, [showEditOption, selectedData, setValue]);

  const dutyDate =
    selectedData?.attendance_type === "Comp off"
      ? selectedData?.requested_date
      : selectedData?.date;

  // Gated on status alone before -- a view-only user could cancel a
  // retrospective off-day duty request with no permission check at all (the
  // New Request button on OffDayDuty/index.js already gates creation on
  // this same checkAccess).
  const canCancel =
    checkAccess && !showEditOption && selectedData?.status === "Pending";

  const displayData = normalizeApprovalPersonFields({
    ...selectedData,
    employee_name:
      selectedData?.employee_name ||
      selectedData?.user_name ||
      selectedData?.requested_by ||
      userDetails?.first_name,
    employee_id: selectedData?.employee_id || selectedData?.emp_id || userDetails?.employee_id,
    // action_by isn't a status-specific "who approved/rejected/cancelled
    // this" field -- falling back to it here for all three would let
    // whichever generic "last touched by" value exists leak into the wrong
    // one of the three, the same class of bug as approver_name elsewhere.
    approved_by: selectedData?.approved_by,
    rejected_by: selectedData?.rejected_by,
    cancelled_by: selectedData?.cancelled_by,
  });

  const employeeDetails = {
    image_url: selectedData?.image_url || userDetails?.image_url,
    employee_id:
      selectedData?.employee_id ||
      selectedData?.emp_id ||
      userDetails?.employee_id ||
      "-",
    user_name: displayData.employee_name || "-",
    department_name:
      selectedData?.department_name ||
      selectedData?.department ||
      userDetails?.department_name ||
      "-",
    designation:
      selectedData?.designation_name ||
      selectedData?.designation ||
      userDetails?.designation ||
      "-",
  };

  const timelineData = {
    ...selectedData,
    applied_by: displayData.applied_by || employeeDetails.user_name,
    applied_on: formatDayOffDateTime(
      selectedData?.applied_on,
      selectedData?.requested_on,
      selectedData?.created_at,
      selectedData?.created_on,
    ),
    approved_by: displayData.approved_by,
    approved_at: formatDayOffDateTime(
      selectedData?.approved_at,
      selectedData?.approved_on,
      selectedData?.action_at,
    ),
    rejected_by: displayData.rejected_by,
    rejected_at: formatDayOffDateTime(
      selectedData?.rejected_at,
      selectedData?.rejected_on,
      selectedData?.action_at,
    ),
    cancelled_by: displayData.cancelled_by,
    cancelled_at: formatDayOffDateTime(
      selectedData?.cancelled_at,
      selectedData?.cancelled_on,
      selectedData?.action_at,
    ),
  };

  const handleCancelRequest = async () => {
    setCancelling(true);
    const response = await customAPI(
      `${domain}my_zone/apply/day_off/myworkspace-dayoff-approval/?company_id=${requestCompanyId}`,
      "PUT",
      {
        approval_id: selectedData?.approval_id,
        record_type: selectedData?.record_type,
        status: "Cancelled",
        cancelled_reason: cancelReason.trim(),
      },
    );
    setCancelling(false);
    closeCancelDialog();
    if (response?.ok) {
      showToast("success", response?.responseDetails?.message);
      onUpdated?.();
      onClose();
    }
    // On failure customAPI already surfaces the error toast.
  };

  const closeCancelDialog = () => {
    setShowCancelDialog(false);
    setCancelReason("");
    setCancelReasonError("");
  };

  const submitCancelRequest = () => {
    const trimmed = cancelReason.trim();
    if (!trimmed) {
      setCancelReasonError("Cancel reason is required");
      return;
    }
    if (trimmed.length < 3) {
      setCancelReasonError(minimum3Characters);
      return;
    }
    handleCancelRequest();
  };

  const onSubmit = async (data) => {
    setValue("button_loader", true);
    const response = await customAPI(
      `${domain}my_zone/apply/day_off/myworkspace-dayoff-approval/?company_id=${requestCompanyId}`,
      "PUT",
      {
        approval_id: selectedData?.approval_id,
        record_type: selectedData?.record_type,
        date: watch("date")?.format("YYYY-MM-DD"),
        day_type: data?.dayoff_type,
        reason: data?.reason_for_dayoff,
      },
    );
    setValue("button_loader", false);
    if (response?.ok) {
      showToast("success", response?.responseDetails?.message);
      reset();
      setShowEditOption(false);
      onUpdated?.();
      onClose();
    }
    // On failure customAPI already surfaces the error toast.
  };

  const selectDayoffType = (event, data) => {
    if (data) {
      setValue("dayoff_type", data?.label);
    }
  };

  const handleReason = (event) => {
    if (event.target.value.length <= 1024) {
      setValue("reason_for_dayoff", event.target.value);
    }
  };

  const editAction =
    checkAccess && selectedData?.status === "Pending" ? (
      <Tooltip title="Edit" arrow slotProps={tooltipStyleProps}>
        <IconButton onClick={() => setShowEditOption(true)}>
          <Edit />
        </IconButton>
      </Tooltip>
    ) : null;

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      title={showEditOption ? undefined : "Off-Day Duty Request"}
      subtitle={showEditOption ? undefined : "Request details and status"}
      header={
        showEditOption ? (
          <RequestEditHeader title="Off-Day Duty Request" />
        ) : undefined
      }
      headerActions={showEditOption ? null : editAction}
      footer={
        showEditOption ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1.5,
              width: "100%",
            }}
          >
            <SecondaryButton
              onClick={() => setShowEditOption(false)}
              disabled={Boolean(watch("button_loader"))}
            >
              Discard
            </SecondaryButton>
            <PrimaryButton
              type="submit"
              form="post-dayoff-request-form"
              loading={Boolean(watch("button_loader"))}
              disabled={Boolean(watch("button_loader"))}
            >
              Update
            </PrimaryButton>
          </Box>
        ) : canCancel ? (
          <RequestCancelButton onClick={() => setShowCancelDialog(true)} />
        ) : null
      }
    >
      <GlobalConfirmDialog
        open={showCancelDialog}
        onCancel={closeCancelDialog}
        onConfirm={submitCancelRequest}
        title="Cancel Request"
        confirmLabel="Cancel Request"
        cancelLabel="Keep Request"
        loading={cancelling}
        loadingLabel="Cancelling..."
        danger
        reasonValue={cancelReason}
        onReasonChange={(value) => {
          setCancelReason(value);
          if (cancelReasonError) setCancelReasonError("");
        }}
        reasonError={cancelReasonError}
        reasonPlaceholder="Provide a reason for cancelling this off-day duty request"
      >
        Enter a reason before cancelling this off-day duty request.
      </GlobalConfirmDialog>
      <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
        <RequestDetailHeader
          userDetails={employeeDetails}
          status={selectedData?.status}
          colorCode={selectedData?.color_code}
          variant="employeeDetailsCard"
        />

        {showEditOption ? null : (
          <>
            <RequestDetailsSection
              fields={[
                { icon: AccessTime, label: "Date", value: dutyDate || "-", accent: detailAccentPalette.indigo },
                { icon: AccessTime, label: "Applied On", value: timelineData.applied_on || "-", accent: detailAccentPalette.blue },
                { icon: EventNote, label: "Day Type", value: selectedData?.day_type || "-", accent: detailAccentPalette.pink },
                ...(selectedData?.reason ? [{ icon: DescriptionOutlinedIcon, label: "Reason", value: selectedData.reason, accent: detailAccentPalette.orange, multiline: true }] : []),
                ...(selectedData?.rejected_reason ? [{ icon: DescriptionOutlinedIcon, label: "Reject Reason", value: selectedData.rejected_reason, accent: detailAccentPalette.purple, multiline: true }] : []),
                ...(selectedData?.cancelled_reason ? [{ icon: DescriptionOutlinedIcon, label: "Cancel Reason", value: selectedData.cancelled_reason, accent: detailAccentPalette.purple, multiline: true }] : []),
              ]}
            />

            <RequestStatusTimeline endDate={selectedData?.date} data={timelineData} hidePendingStep />
          </>
        )}
      </Box>

      {showEditOption ? (
        <form id="post-dayoff-request-form" onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ p: 2 }}>
            <Box sx={childBoxStyle}>
              <Box>
                <Typography sx={{ fontWeight: 600 }}>Off-Day Date</Typography>
                <Typography {...infoStyle} sx={{ ...infoStyle.sx, mb: 0 }}>
                  Update the date for the off-day duty period
                </Typography>
                <CustomizedDatePicker
                  name="date"
                  control={control}
                  required
                  requiredMessage="Date is required"
                  placeholder="Select date"
                />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 600 }}>Off-Day Type</Typography>
                <Typography {...infoStyle} sx={{ ...infoStyle.sx, mb: 0.5 }}>
                  Update the off-day type for further reference
                </Typography>
                <CustomAutoComplete
                  dataList={dayoffType}
                  rules={{
                    ...register("dayoff_type", {
                      required: "Off-day type is required",
                    }),
                  }}
                  selectedValue={watch("dayoff_type")}
                  updateDataFunction={selectDayoffType}
                  placeHolder="Select off-day type"
                  errorMessage={errors?.dayoff_type?.message || ""}
                  textBoxError={Boolean(errors?.dayoff_type)}
                />
              </Box>

              <Box>
                <Typography sx={{ fontWeight: 600 }}>Reason</Typography>
                <Typography {...infoStyle}>
                  Enter the reason for this off-day duty request
                </Typography>
                <CustomTextBox
                  type="multiline"
                  rules={{
                    ...register("reason_for_dayoff", {
                      required: "Reason is required",
                      validate: {
                        minLengthNoSpaces: (value) => {
                          if (value?.trim().length < 3 && value !== "") {
                            return minimum3Characters;
                          } else if (value?.trim().length > 255 && value !== "") {
                            return "Maximum length is 255 characters";
                          } else {
                            return true;
                          }
                        },
                        atleastOneCharacterWithSpaceReg: (value) => {
                          if (atleastOneCharacterWithSpace.test(value) || value === "") {
                            return true;
                          } else {
                            return "Atleast one alphabet required";
                          }
                        },
                      },
                    }),
                  }}
                  selectedValue={watch("reason_for_dayoff")}
                  updateDataFunction={handleReason}
                  placeHolder="Provide a reason for off-day duty"
                  errorMessage={errors?.reason_for_dayoff?.message || ""}
                  textBoxError={Boolean(errors?.reason_for_dayoff)}
                />
              </Box>
            </Box>
          </Box>
        </form>
      ) : null}
    </GlobalSidePanel>
  );
};

export default ViewDayOffPostApproval;
